import {
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnInit,
	AfterViewInit,
	SimpleChanges,
	ViewChild,
	forwardRef
} from '@angular/core';
import {
	ControlValueAccessor,
	NgModel,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	Validator,
	ValidationErrors,
} from '@angular/forms';
import { FormatWidth, FormStyle, getLocaleDayPeriods, getLocaleTimeFormat, TranslationWidth } from '@angular/common';

import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { DateUtil } from 'rev-shared/date/DateUtil';
import { TimezoneService } from 'rev-shared/date/Timezone.Service';
import { isDate, isNumber } from 'rev-shared/util';

import { NgxLocaleLoader } from '../datePicker/NgxLocaleLoader.Service';

import { TranslateService } from '@ngx-translate/core';

import styles from './vb-ui-date-time-picker.module.less';

interface IDateTimePickerViewValue {
	date: Date;
	time: Date;
	timezoneId: string;
	tzOffset: number;
	utcTimestamp: number;
}

let vbDateTimePickerIndex = 0;

@Component({
	selector: 'vb-date-time-picker',
	templateUrl: './VbDateTimePicker.Component.html',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VbDateTimePickerComponent),
			multi: true
		}, {
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => VbDateTimePickerComponent),
			multi: true
		}
	],
	standalone: false
})
export class VbDateTimePickerComponent implements ControlValueAccessor, OnChanges, OnInit, AfterViewInit, Validator {
	@Input() public accessibilityLabel: string = '';
	@Input() public timezoneId: string = '';
	@Input() public minuteStep: number = 5;

	@ViewChild('vbDTPDate') public dateInput: NgModel | null = null;
	@ViewChild('vbDTPTime') public timeInput: NgModel | null = null;
	public readonly styles = styles;
	public readonly index = ++vbDateTimePickerIndex;

	public exampleTime: Date | null = null;
	public internalModel: IDateTimePickerViewValue = {} as IDateTimePickerViewValue;
	public isTimeValid: boolean = false;
	public is24Hr: boolean = false;
	public meridiems: string[] | null = null;
	public status: { [key: string] : boolean } = { active: true };
	public timeLabels: string[] = ['', '', '', ''];

	private onChange: ((value: any) => void) | null = null;
	private onTouched: (() => void) | null = null;

	constructor(
		private readonly TimezoneService: TimezoneService,
		private readonly NgxLocaleLoader: NgxLocaleLoader,
		private readonly el: ElementRef,
		private readonly translate: TranslateService
	) {}

	public validate(): ValidationErrors {
		return { ...this.dateInput?.errors, ...this.validateTimeInput() };
	}

	private validateTimeInput(): ValidationErrors | null {
		return !this.isTimeValid ?
			{ time: true } :
			null;
	}

	public ngOnInit(): void {
		if (!this.accessibilityLabel) {
			console.warn('vb-date-time-picker missing aria-label');
		}

		this.exampleTime = DateUtil.getToday();
		this.exampleTime.setHours(14);
		this.NgxLocaleLoader.load();
		this.is24Hr = !getLocaleTimeFormat(BootstrapContext.bcp47Locale, FormatWidth.Full).includes('a');
		this.meridiems = getLocaleDayPeriods(BootstrapContext.bcp47Locale, FormStyle.Standalone, TranslationWidth.Short).flat();
		this.timeLabels = [
			this.translate.instant('HourIncrement'),
			this.translate.instant('MinuteIncrement'),
			this.translate.instant('HourDecrement'),
			this.translate.instant('MinuteDecrement')
		];
	}

	public ngAfterViewInit(): void {
		let buttonIndex = 0;
		this.el.nativeElement.querySelectorAll('a[href]').forEach((el: HTMLAnchorElement) => {
			//Prevent CSP Errors from these javascript:void(0) links
			el.removeAttribute('href');
			el.setAttribute('role', 'button');
			el.setAttribute('tabindex', '0');
			el.addEventListener('keydown', (event: KeyboardEvent) => {
				if (event.key === 'Enter' || event.key === ' ') {
					event.preventDefault();
					el.click();
				}
			});
			if (this.accessibilityLabel) {
				el.setAttribute('aria-label', `${this.accessibilityLabel} ${this.timeLabels[buttonIndex]}`);
			}
			buttonIndex++;
		});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (changes.timezoneId) {
			this.onTimezoneIdChange();
		}
	}

	public writeValue(value: Date): void {
		this.format(value);
		if (value) {
			this.onTouched?.();
		}
	}

	public registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	private format(modelValue: Date): Date {
		if (modelValue) {
			// update the internal model based on the ngModel modelValue + offset
			this.getUTCOffset(modelValue)
				.then((offset: number) => {
					const modelTime: number = modelValue.getTime();
					const tzOffset: number = DateUtil.getOffset(modelValue) - offset;
					const adjustedDate: Date = new Date(modelTime - tzOffset);
					this.internalModel = {
						date: adjustedDate,
						time: adjustedDate,
						timezoneId: this.timezoneId,
						tzOffset,
						utcTimestamp: modelTime
					};
				});
		}

		// pass-through
		return modelValue;
	}

	private getUTCOffset(date: Date): Promise<number> {
		if (this.timezoneId) {
			if (date) {
				return this.TimezoneService.getUTCOffset(this.timezoneId, [date], true)
					.then((offsets: any[]) => offsets[0]);
			}

			return Promise.resolve(0);
		}

		return Promise.resolve( DateUtil.getOffset(date) );

	}

	public onChildChange(): void {
		this.updateFromInternalModel();
		this.onTouched?.();
	}

	private onTimezoneIdChange(): void {
		if (!this.internalModel.date) {
			return;
		}

		this.onChildChange();
	}

	private parse(withOffset: boolean): Date | null {
		const viewValue = this.internalModel;
		const date = viewValue.date;
		const hours = viewValue.time?.getHours();
		const minutes = viewValue.time?.getMinutes();
		const offset = withOffset ? viewValue.tzOffset || 0 : 0;

		if (!isDate(date) ||
			!date.getTime() ||
			!isNumber(hours) ||
			!isNumber(minutes)) {
			return null;
		}
		date.setHours(hours);
		date.setMinutes(minutes);
		return new Date(date.getTime() + offset);
	}

	private updateFromInternalModel(): void {
		const internalViewDate: Date | null = this.parse(false);

		if (!internalViewDate) {
			this.onChange?.(null);
			return;
		}

		this.getUTCOffset(internalViewDate)
			.then(offset => {
				// apply the newly retrieved offset to the internal model
				this.internalModel.tzOffset = DateUtil.getOffset(internalViewDate) - offset;

				// update ngModel with parsed value factoring in the new offset
				this.onChange?.(this.parse(true));
			});
	}

	public setTimeValid(isValid: boolean) {
		this.isTimeValid = isValid;
	}
}
